import React, { useState, useEffect, useMemo } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { fetchMovies, fetchShowdown } from '../api/client';
import ChartCard from '../components/ChartCard';
import RankingTable from '../components/RankingTable';
import Poster from '../components/Poster';
import OccBar from '../components/OccBar';

const formatNumber = (v) => (v || 0).toLocaleString();
const COLORS = ['#2563eb', '#dc2626', '#16a34a', '#d97706'];
const WEEKDAYS = ['א׳', 'ב׳', 'ג׳', 'ד׳', 'ה׳', 'ו׳', 'ש׳']; // Israel: Sun…Sat

const ALIGN = [
  { key: 'day_number', label: 'לפי יום מהיציאה' },
  { key: 'date', label: 'לפי תאריך' },
  { key: 'day_of_week', label: 'לפי יום בשבוע' },
];

const cardStyle = { background: 'var(--surf)', borderRadius: 16, boxShadow: 'var(--shadow)', padding: 20 };

function ShowdownPage({ seedMovieId, refreshNonce }) {
  const [movies, setMovies] = useState([]);
  const [selectedIds, setSelectedIds] = useState(seedMovieId ? [seedMovieId] : []);
  const [align, setAlign] = useState('day_number');
  const [cumulative, setCumulative] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  // Movie catalog for the picker (sorted by tickets in the endpoint)
  useEffect(() => {
    fetchMovies().then(setMovies).catch(() => setMovies([]));
  }, [refreshNonce]);

  // Re-seed if navigated in with a movie
  useEffect(() => {
    if (seedMovieId) setSelectedIds(prev => (prev.includes(seedMovieId) ? prev : [seedMovieId, ...prev].slice(0, 4)));
  }, [seedMovieId]);

  // Fetch the comparison whenever the selection / alignment changes
  useEffect(() => {
    if (selectedIds.length < 2) { setData(null); return; }
    setLoading(true);
    fetchShowdown(selectedIds, align)
      .then(d => { setData(d); setLoading(false); })
      .catch(() => { setData(null); setLoading(false); });
  }, [selectedIds, align, refreshNonce]);

  const movieById = useMemo(() => {
    const m = {};
    movies.forEach(mv => { m[mv.id] = mv; });
    return m;
  }, [movies]);

  const addMovie = (id) => {
    setSelectedIds(prev => (prev.includes(id) || prev.length >= 4 ? prev : [...prev, id]));
    setSearch('');
  };
  const removeMovie = (id) => setSelectedIds(prev => prev.filter(x => x !== id));

  const searchResults = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    return movies
      .filter(mv => !selectedIds.includes(mv.id))
      .filter(mv => ((mv.title_he || '') + ' ' + (mv.title || '')).toLowerCase().includes(q))
      .slice(0, 8);
  }, [search, movies, selectedIds]);

  const showCumulative = cumulative && align !== 'day_of_week';

  // Merge each movie's series onto a shared x-axis
  const chartData = useMemo(() => {
    if (!data?.movies) return [];
    const map = {};
    data.movies.forEach((mv, mi) => {
      (mv.series || []).forEach(pt => {
        if (!map[pt.x]) map[pt.x] = { x: pt.x };
        map[pt.x][`m${mi}`] = showCumulative && pt.cumulative != null ? pt.cumulative : pt.tickets;
      });
    });
    return Object.values(map).sort((a, b) =>
      typeof a.x === 'number' ? a.x - b.x : String(a.x).localeCompare(String(b.x)));
  }, [data, showCumulative]);

  const xTick = (v) => {
    if (align === 'day_of_week') return WEEKDAYS[v] ?? v;
    if (align === 'date') return typeof v === 'string' ? v.slice(5) : v;
    return `יום ${v}`;
  };

  const statColumns = [
    {
      key: 'title', header: 'סרט', align: 'right', sortValue: r => r.title_he || r.title || '',
      render: (r, _rank, idx) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ width: 10, height: 10, borderRadius: 3, background: r.__color, flexShrink: 0 }} />
          <Poster posterUrl={r.poster_url} genre={r.genre} letter={(r.title_he || r.title || '?')[0]} size="sm" />
          <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--txt)' }}>{r.title_he || r.title}</div>
        </div>
      ),
    },
    { key: 'total_tickets', header: 'סה״כ כרטיסים', align: 'center',
      render: r => <span style={{ fontWeight: 700, color: 'var(--accent)' }}>{formatNumber(r.total_tickets)}</span> },
    { key: 'opening_tickets', header: 'סופ״ש פתיחה', align: 'center',
      render: r => <span>{formatNumber(r.opening_tickets)}</span> },
    { key: 'avg_occupancy', header: 'תפוסה ממוצעת', align: 'center',
      render: r => <div style={{ display: 'inline-flex' }}><OccBar value={r.avg_occupancy} /></div> },
    { key: 'peak_tickets', header: 'שיא יומי', align: 'center',
      render: r => <span>{formatNumber(r.peak_tickets)}</span> },
    { key: 'weeks_in_release', header: 'שבועות', align: 'center',
      render: r => <span style={{ color: 'var(--muted)' }}>{r.weeks_in_release || '—'}</span> },
  ];

  const statRows = (data?.movies || []).map((mv, mi) => ({ ...mv, __color: COLORS[mi % COLORS.length] }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Movie picker */}
      <div style={cardStyle}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--muted)', marginBottom: 10 }}>בחר 2–4 סרטים להשוואה</div>

        {/* Selected chips */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          {selectedIds.map((id, mi) => {
            const mv = movieById[id] || (data?.movies || []).find(m => m.movie_id === id);
            const title = mv ? (mv.title_he || mv.title) : `#${id}`;
            return (
              <div key={id} style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px',
                borderRadius: 10, border: `1px solid ${COLORS[mi % COLORS.length]}55`,
                background: `${COLORS[mi % COLORS.length]}14`,
              }}>
                <span style={{ width: 9, height: 9, borderRadius: 3, background: COLORS[mi % COLORS.length] }} />
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--txt)' }}>{title}</span>
                <button onClick={() => removeMovie(id)} style={{
                  border: 'none', background: 'none', cursor: 'pointer', color: 'var(--faint)', fontSize: 15, lineHeight: 1,
                }}>×</button>
              </div>
            );
          })}
          {selectedIds.length === 0 && <span style={{ fontSize: 13, color: 'var(--faint)' }}>לא נבחרו סרטים</span>}
        </div>

        {/* Search */}
        {selectedIds.length < 4 && (
          <div style={{ position: 'relative', maxWidth: 360 }}>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="חיפוש סרט להוספה…"
              style={{
                width: '100%', boxSizing: 'border-box', padding: '8px 12px', borderRadius: 8,
                border: '1px solid var(--brd)', background: 'var(--surf-alt)', color: 'var(--txt)',
                fontSize: 13, fontFamily: 'Heebo, sans-serif', outline: 'none',
              }}
            />
            {searchResults.length > 0 && (
              <div style={{
                position: 'absolute', top: '100%', insetInlineStart: 0, insetInlineEnd: 0, zIndex: 20,
                marginTop: 4, background: 'var(--surf)', border: '1px solid var(--brd)', borderRadius: 10,
                boxShadow: 'var(--shadow)', overflow: 'hidden',
              }}>
                {searchResults.map(mv => (
                  <button key={mv.id} onClick={() => addMovie(mv.id)} style={{
                    display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '8px 12px',
                    border: 'none', borderBottom: '1px solid var(--brd)', background: 'none', cursor: 'pointer',
                    fontFamily: 'Heebo, sans-serif', textAlign: 'right',
                  }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--surf-hov)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                    <Poster posterUrl={mv.poster_url} genre={mv.genre} letter={(mv.title_he || mv.title || '?')[0]} size="sm" />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--txt)' }}>{mv.title_he || mv.title}</div>
                      <div style={{ fontSize: 11, color: 'var(--faint)' }}>{formatNumber(mv.total_tickets_sold)} כרטיסים</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {selectedIds.length < 2 ? (
        <div style={{ ...cardStyle, textAlign: 'center', color: 'var(--faint)', padding: 40 }}>
          בחר לפחות שני סרטים כדי להתחיל בהשוואה
        </div>
      ) : (
        <>
          {/* Alignment + cumulative toggle */}
          <div style={{ ...cardStyle, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: 6 }}>
              {ALIGN.map(a => {
                const on = a.key === align;
                return (
                  <button key={a.key} onClick={() => setAlign(a.key)} style={{
                    padding: '7px 14px', borderRadius: 9, fontSize: 13, fontWeight: on ? 700 : 400,
                    fontFamily: 'Heebo, sans-serif', cursor: 'pointer',
                    border: `1px solid ${on ? 'var(--accent)' : 'var(--brd)'}`,
                    background: on ? 'var(--accent)' : 'var(--surf-alt)', color: on ? '#fff' : 'var(--muted)',
                  }}>{a.label}</button>
                );
              })}
            </div>
            {align !== 'day_of_week' && (
              <button onClick={() => setCumulative(c => !c)} style={{
                padding: '7px 14px', borderRadius: 9, fontSize: 13, fontFamily: 'Heebo, sans-serif', cursor: 'pointer',
                border: `1px solid ${cumulative ? 'var(--accent)' : 'var(--brd)'}`,
                background: cumulative ? 'var(--accent)' : 'var(--surf-alt)', color: cumulative ? '#fff' : 'var(--muted)',
              }}>{cumulative ? 'מצטבר' : 'יומי'}</button>
            )}
          </div>

          {/* Chart */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: 60, color: 'var(--faint)' }}>טוען השוואה...</div>
          ) : (
            <>
              <ChartCard title={`כרטיסים ${showCumulative ? 'מצטברים' : 'יומיים'} — השוואה`}>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={chartData} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                    <CartesianGrid stroke="var(--brd)" vertical={false} />
                    <XAxis dataKey="x" tick={{ fill: 'var(--faint)', fontSize: 10 }} tickLine={false} axisLine={false}
                      tickFormatter={xTick} />
                    <YAxis tick={{ fill: 'var(--faint)', fontSize: 10 }} tickLine={false} axisLine={false}
                      tickFormatter={v => v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v} />
                    <Tooltip
                      contentStyle={{ background: 'var(--surf)', border: 'none', borderRadius: 12, boxShadow: 'var(--shadow)', color: 'var(--txt)', fontFamily: 'Heebo,sans-serif', fontSize: 13 }}
                      formatter={(v, name) => [formatNumber(v), name]}
                      labelFormatter={xTick} />
                    <Legend wrapperStyle={{ fontSize: 12, fontFamily: 'Heebo,sans-serif' }} />
                    {(data?.movies || []).map((mv, mi) => (
                      <Line key={mv.movie_id} type="monotone" dataKey={`m${mi}`}
                        name={mv.title_he || mv.title} stroke={COLORS[mi % COLORS.length]}
                        strokeWidth={2} dot={false} activeDot={{ r: 5, strokeWidth: 0 }} connectNulls />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </ChartCard>

              {/* Side-by-side stats */}
              <RankingTable columns={statColumns} data={statRows} initialSort={{ key: 'total_tickets', dir: 'desc' }} minWidth={620} />
            </>
          )}
        </>
      )}
    </div>
  );
}

export default ShowdownPage;
